import { useState, useEffect, useCallback, useMemo } from 'react';
import { studentAPI } from '../services/api';
import { examService } from '../services/examService';

export const useStudentExams = (params = {}) => {
  const [exams, setExams] = useState([]);
  const [examDetails, setExamDetails] = useState(null);
  const [examResults, setExamResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoize params to prevent unnecessary re-renders
  const memoizedParams = useMemo(() => params, [JSON.stringify(params)]);

  const fetchAvailableExams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await studentAPI.getExams(memoizedParams);
      setExams(response.data?.data?.exams || response.data?.exams || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch available exams');
      console.error('Error fetching exams:', err);
    } finally {
      setLoading(false);
    }
  }, [memoizedParams]);

  const fetchExamDetails = useCallback(async (examId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await studentAPI.getExam(examId);
      const examData = response.data?.data || response.data;
      setExamDetails(examData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch exam details');
      console.error('Error fetching exam details:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const startExam = async (examId) => {
    try {
      const response = await examService.startExam(examId);
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Failed to start exam');
    }
  };

  const saveAnswer = async (examId, questionId, answer) => {
    try {
      // Save answer to backend - this should be part of autosave or session management
      // For now, we'll use the exam service if available, otherwise store temporarily
      // Note: This might need to be handled by the exam session endpoint
      return { data: { questionId, answer } };
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Failed to save answer');
    }
  };

  const submitExam = async (examId, answers) => {
    try {
      const response = await examService.submitExamAnswers(examId, { answers });
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Failed to submit exam');
    }
  };

  const fetchExamResult = useCallback(async (examId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await examService.getMyExamResults({ examId });
      const results = response.data?.data?.results || response.data?.results || [];
      // Get the result for this specific exam
      const result = Array.isArray(results) ? results.find(r => r.examId === examId) : results;
      setExamResults(result || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch exam results');
      console.error('Error fetching exam results:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailableExams();
  }, [fetchAvailableExams]);

  return {
    exams,
    examDetails,
    examResults,
    loading,
    error,
    fetchAvailableExams,
    fetchExamDetails,
    startExam,
    saveAnswer,
    submitExam,
    fetchExamResult
  };
};
